import { Form, Formik } from 'formik'
import { observer } from 'mobx-react-lite'
import { ReactElement, useMemo, useRef } from 'react'
import { AddGroupPart } from 'src/store/lists/interfaces'
import { defaultTheme } from 'src/theme'
import { breakpoints } from 'src/theme/breakpoints'
import { zIndex } from 'src/theme/zIndex'
import styled from 'styled-components'
import Table from './Table'
import { useListsStore } from 'src/store/lists/ListsStore'
import { I18n } from 'src/i18n'
import { useAddToListStore } from 'src/store/lists/AddToListStore'
import Wizard from '../../../../Wizard'
import Breadcrumb from '../../../../Breadcrumb/Breadcrumb'
import ErrorMessage from '../Errors/ValidationErrorMsg'

const getDefaultRow = () => ({
  lineCode: '',
  partNumber: '',
  stockQty: 1,
  comment: '',
})

const initialValues: Array<AddGroupPart> = [...Array(5)].map(getDefaultRow)

const shouldAddNewRow = (rows: AddGroupPart[]) => {
  const lastItem = rows?.at(-1)
  if (!!lastItem?.partNumber || !!lastItem?.comment || lastItem?.stockQty !== 1)
    return true
  return false
}

const AddForm = observer((): ReactElement => {
  const listStore = useListsStore()
  const addToListStore = useAddToListStore()
  const formRef = useRef(null)
  const categoryLabel = listStore.getSelectedCategoryLabel()
  const listId = listStore.getSelectedListId()
  const listLabel = listStore.getSelectedListLabel()
  const selectedGroup = listStore.selectedGroup?.value
  const error = addToListStore.getError()

  const breadCrumbItems = useMemo(
    () => [
      { text: categoryLabel },
      { text: listLabel },
      { text: selectedGroup },
    ],
    [categoryLabel, listLabel, selectedGroup]
  )

  const handleOnSubmit = (data): void => {
    const groupedItems = addToListStore.groupItems(selectedGroup, data.items)
    const validate = async () =>
      await addToListStore.validateItems(listId, groupedItems)

    addToListStore.runValidation(validate)
  }

  const goNext = async (): Promise<void> => {
    await formRef.current.submitForm()
  }

  const goBackLink = {
    text: 'back',
    action: () => {
      addToListStore.setError(null)
      addToListStore.previousStage()
    },
  }

  const goNextLink = {
    text: 'next',
    action: goNext,
  }

  const clearError = () => {
    addToListStore.setError(null)
  }

  return (
    <Wizard goBack={goBackLink} goNext={goNextLink}>
      <Wrapper>
        <Breadcrumb items={breadCrumbItems} />
        <Instructions>
          <I18n name="addTheItemsYouWishToIncludeInThisGroup" />
        </Instructions>
        {error && <ErrorMessage onClose={clearError} error={error} />}
        <Container>
          <Formik
            initialValues={{ items: initialValues }}
            onSubmit={handleOnSubmit}
            innerRef={formRef}
          >
            {({ values }) => {
              const newRow = shouldAddNewRow(values?.items)
              if (newRow) values.items.push(getDefaultRow())
              return (
                <Form>
                  <Table />
                </Form>
              )
            }}
          </Formik>
        </Container>
      </Wrapper>
    </Wizard>
  )
})

const Wrapper = styled.div``

const Container = styled.div`
  margin: 1rem 0rem 1rem 0rem;
  width: 100%;
  background-color: ${defaultTheme.colors.componentsBackground};
  z-index: ${zIndex.modal};
  @media screen and (max-width: ${breakpoints.tabletLarge}px) {
    width: 100%;
  }
`

const Instructions = styled.div`
  font-size: 14px;
  font-weight: 400;
  line-height: 24px;
  text-align: left;
`

export default AddForm
